import { useEffect, useState } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

let cachedCountries = null;
let inflight = null;

/**
 * Shared loader for Vughy public countries via our API proxy:
 * GET /api/countries → https://api.vughy.com/api/public/countries (VUGHY_API_KEY)
 */
export const loadCountries = async ({ forceRefresh = false } = {}) => {
  if (!forceRefresh && Array.isArray(cachedCountries) && cachedCountries.length) {
    return cachedCountries;
  }

  if (!forceRefresh && inflight) {
    return inflight;
  }

  inflight = axios
    .get(`${API_URL}/countries`, {
      params: forceRefresh ? { refresh: 1, _t: Date.now() } : undefined,
    })
    .then((res) => {
      const list = Array.isArray(res.data) ? res.data : [];
      cachedCountries = list;
      return list;
    })
    .finally(() => {
      inflight = null;
    });

  return inflight;
};

export const useCountries = ({ enabled = true, forceRefresh = false } = {}) => {
  const [countries, setCountries] = useState(
    () => (enabled && Array.isArray(cachedCountries) ? cachedCountries : [])
  );
  const [loading, setLoading] = useState(Boolean(enabled && !cachedCountries?.length));
  const [error, setError] = useState('');

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return undefined;
    }

    let cancelled = false;

    const run = async () => {
      setLoading(true);
      setError('');
      try {
        const list = await loadCountries({ forceRefresh });
        if (!cancelled) setCountries(list);
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.error || err.message || 'Failed to load countries');
          if (!cachedCountries?.length) setCountries([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [enabled, forceRefresh]);

  return { countries, loading, error, reload: () => loadCountries({ forceRefresh: true }) };
};

export default useCountries;
